import { useMemo, useState, useCallback, useEffect } from 'react';
import type { Client } from '@/lib/types';
import { useClientsCache } from '@/context/clients-cache-context';
import { clientSearchHaystack } from '@/lib/clients/roster-fields';
import {
  getClientClassificationTags,
  clientMatchesClassificationFilterIds,
  countActiveClassificationFilters,
  type ClassificationTagId,
  type ClientClassificationCacheSlice,
} from '@/lib/clients/classification-tags';
import { needsTagKey } from '@/lib/clients/needs-tags';
import { safeToDate } from '@/lib/utils';
import {
  buildRosterIntelligenceFromSummaries,
  type ClientIntelligence,
} from '@/lib/clients/roster-intelligence';

export type RosterSortField = 'name' | 'email' | 'createdAt' | 'attention';
export type RosterSortDirection = 'asc' | 'desc';
export const ROSTER_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export interface RosterQueryState {
  search: string;
  sortField: RosterSortField;
  sortDirection: RosterSortDirection;
  page: number;
  pageSize: number;
  classificationFilters: ClassificationTagId[];
  /** Canonical `category::value` needs-tag keys to filter by (AND). */
  needsFilters: string[];
}

export const DEFAULT_ROSTER_QUERY_STATE: RosterQueryState = {
  search: '',
  // Phase 3.7 — inbox-zero default: surface the clients that need attention first.
  sortField: 'attention',
  sortDirection: 'desc',
  page: 1,
  pageSize: 10,
  classificationFilters: [],
  needsFilters: [],
};

export const ROSTER_QUERY_STORAGE_KEY = 'harry_client_roster_query_state';

export function loadStoredRosterQueryState(): RosterQueryState {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return DEFAULT_ROSTER_QUERY_STATE;
  }
  try {
    const raw = window.sessionStorage.getItem(ROSTER_QUERY_STORAGE_KEY);
    if (!raw) return DEFAULT_ROSTER_QUERY_STATE;

    const parsed = JSON.parse(raw);
    const validSortFields: RosterSortField[] = ['name', 'email', 'createdAt', 'attention'];
    const validSortDirections: RosterSortDirection[] = ['asc', 'desc'];
    const validPageSizes = ROSTER_PAGE_SIZE_OPTIONS as readonly number[];

    return {
      search: typeof parsed.search === 'string' ? parsed.search : DEFAULT_ROSTER_QUERY_STATE.search,
      sortField: validSortFields.includes(parsed.sortField)
        ? parsed.sortField
        : DEFAULT_ROSTER_QUERY_STATE.sortField,
      sortDirection: validSortDirections.includes(parsed.sortDirection)
        ? parsed.sortDirection
        : DEFAULT_ROSTER_QUERY_STATE.sortDirection,
      page:
        typeof parsed.page === 'number' && parsed.page > 0
          ? Math.floor(parsed.page)
          : DEFAULT_ROSTER_QUERY_STATE.page,
      pageSize: validPageSizes.includes(parsed.pageSize)
        ? parsed.pageSize
        : DEFAULT_ROSTER_QUERY_STATE.pageSize,
      classificationFilters: Array.isArray(parsed.classificationFilters)
        ? parsed.classificationFilters.filter((item): item is ClassificationTagId => typeof item === 'string')
        : DEFAULT_ROSTER_QUERY_STATE.classificationFilters,
      needsFilters: Array.isArray(parsed.needsFilters)
        ? parsed.needsFilters.filter((item): item is string => typeof item === 'string')
        : DEFAULT_ROSTER_QUERY_STATE.needsFilters,
    };
  } catch {
    return DEFAULT_ROSTER_QUERY_STATE;
  }
}

export interface RosterClientRow {
  client: Client;
  classificationTagIds: ClassificationTagId[];
  /** Phase 3 clinical signals for this client (flags, next action, attention score). */
  intelligence?: ClientIntelligence;
}

export interface RosterQueryResult {
  rows: RosterClientRow[];
  total: number;
  totalPages: number;
  activeFilterCount: number;
}

function tokenizeSearch(query: string): string[] {
  return query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

export function clientMatchesSearch(client: Client, query: string): boolean {
  const tokens = tokenizeSearch(query);
  if (tokens.length === 0) return true;

  // `as unknown` first: Client (with its optional fields) no longer structurally
  // overlaps Record<string, unknown> enough for a direct assertion.
  const haystack = clientSearchHaystack(client as unknown as Record<string, unknown>).join(' ');
  return tokens.every((token) => haystack.includes(token));
}

function compareClients(
  a: RosterClientRow,
  b: RosterClientRow,
  field: RosterSortField,
  direction: RosterSortDirection
): number {
  let cmp = 0;

  if (field === 'name') {
    cmp = (a.client.name || '').toLowerCase().localeCompare((b.client.name || '').toLowerCase());
  } else if (field === 'email') {
    cmp = (a.client.email || '').toLowerCase().localeCompare((b.client.email || '').toLowerCase());
  } else if (field === 'attention') {
    const scoreA = a.intelligence?.attentionScore ?? 0;
    const scoreB = b.intelligence?.attentionScore ?? 0;
    cmp = scoreA - scoreB;
    // Stable secondary sort by name so equal-attention rows stay deterministic.
    if (cmp === 0) {
      cmp = (a.client.name || '').toLowerCase().localeCompare((b.client.name || '').toLowerCase());
      return direction === 'desc' ? cmp : -cmp;
    }
  } else {
    const timeA = safeToDate(a.client.createdAt)?.getTime() ?? 0;
    const timeB = safeToDate(b.client.createdAt)?.getTime() ?? 0;
    cmp = timeA - timeB;
  }

  return direction === 'asc' ? cmp : -cmp;
}

export function applyRosterQuery(
  clients: Client[],
  state: RosterQueryState,
  cache?: ClientClassificationCacheSlice,
  intelligence?: Map<string, ClientIntelligence>
): RosterQueryResult {
  const enriched: RosterClientRow[] = clients.map((client) => ({
    client,
    classificationTagIds: getClientClassificationTags(client, cache).map((t) => t.id),
    intelligence: intelligence?.get(client.id),
  }));

  const filtered = enriched.filter(({ client, classificationTagIds }) => {
    if (!clientMatchesSearch(client, state.search)) return false;

    if (
      !clientMatchesClassificationFilterIds(
        classificationTagIds,
        state.classificationFilters
      )
    ) {
      return false;
    }

    if (state.needsFilters.length > 0) {
      const needsKeys = new Set((client.needsTags ?? []).map(needsTagKey));
      if (!state.needsFilters.every((key) => needsKeys.has(key))) return false;
    }

    return true;
  });

  const sorted = [...filtered].sort((a, b) =>
    compareClients(a, b, state.sortField, state.sortDirection)
  );

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / state.pageSize));
  const safePage = Math.min(Math.max(1, state.page), totalPages);
  const start = (safePage - 1) * state.pageSize;
  const rows = sorted.slice(start, start + state.pageSize);

  const activeFilterCount =
    countActiveClassificationFilters(state.classificationFilters) + state.needsFilters.length;

  return { rows, total, totalPages, activeFilterCount };
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/** Activity-flag banner only when the summary failed and the roster itself loaded. */
export function rosterActivityFlagsUnavailable(
  summaryFetchError: string | null | undefined,
  clientsFetchError: string | null | undefined,
): boolean {
  return Boolean(summaryFetchError) && !clientsFetchError;
}

export function useRosterQuery() {
  const { clients, clientSummaries, summaryLoading, summaryFetchError, clientsFetchError } =
    useClientsCache();
  const [state, setState] = useState<RosterQueryState>(loadStoredRosterQueryState);
  const debouncedSearch = useDebouncedValue(state.search, 300);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem(ROSTER_QUERY_STORAGE_KEY, JSON.stringify(state));
      } catch {
        // Ignore storage write errors (e.g. quota exceeded)
      }
    }
  }, [state]);

  const queryState = useMemo(
    () => ({ ...state, search: debouncedSearch }),
    [state, debouncedSearch]
  );

  // Roster flags (No assessment / Reassess due / Lapsed) derive from the cache's
  // lightweight per-client summaries. Classification tags read the persisted
  // client.classification (backfilled from posture analyses), so no eager
  // per-client posture array is needed here.
  //
  // A client with no summary is indistinguishable from a client with no data, so
  // flags must not render while summaries are still loading or failed to load —
  // otherwise every client briefly (or permanently, on a backend that lacks
  // /clients/summary) shows a wrong "No assessment" flag.
  const summariesReady = !summaryLoading && !summaryFetchError;
  const intelligence = useMemo(
    () =>
      summariesReady
        ? buildRosterIntelligenceFromSummaries(clients, clientSummaries)
        : undefined,
    [summariesReady, clients, clientSummaries]
  );

  const result = useMemo(
    () => applyRosterQuery(clients, queryState, undefined, intelligence),
    [clients, queryState, intelligence]
  );

  const setSearch = useCallback((search: string) => {
    setState((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const setSort = useCallback((sortField: RosterSortField) => {
    setState((prev) => ({
      ...prev,
      sortField,
      sortDirection:
        prev.sortField === sortField && prev.sortDirection === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setState((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setState((prev) => ({ ...prev, page }));
  }, []);

  const toggleClassificationFilter = useCallback((tagId: ClassificationTagId) => {
    setState((prev) => {
      const has = prev.classificationFilters.includes(tagId);
      const classificationFilters = has
        ? prev.classificationFilters.filter((id) => id !== tagId)
        : [...prev.classificationFilters, tagId];
      return { ...prev, classificationFilters, page: 1 };
    });
  }, []);

  const toggleNeedsFilter = useCallback((needsKey: string) => {
    setState((prev) => {
      const has = prev.needsFilters.includes(needsKey);
      const needsFilters = has
        ? prev.needsFilters.filter((key) => key !== needsKey)
        : [...prev.needsFilters, needsKey];
      return { ...prev, needsFilters, page: 1 };
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setState((prev) => ({
      ...prev,
      classificationFilters: [],
      needsFilters: [],
      page: 1,
    }));
  }, []);

  /**
   * Remove any needs-tag filter keys that are no longer present across the roster.
   * Call with the current union of client needs keys (`category::value`) so that a
   * need removed from the last client holding it doesn't leave a filter that
   * silently empties the roster.
   */
  const removeStaleNeedsFilters = useCallback((validNeedsKeys: string[]) => {
    const valid = new Set(validNeedsKeys);
    setState((prev) => {
      const next = prev.needsFilters.filter((key) => valid.has(key));
      if (next.length === prev.needsFilters.length) return prev;
      return { ...prev, needsFilters: next, page: 1 };
    });
  }, []);

  return {
    ...result,
    state,
    debouncedSearch,
    /** True when activity flags failed and the roster request itself succeeded. */
    flagsUnavailable: rosterActivityFlagsUnavailable(summaryFetchError, clientsFetchError),
    setSearch,
    setSort,
    setPageSize,
    setPage,
    toggleClassificationFilter,
    toggleNeedsFilter,
    clearAllFilters,
    removeStaleNeedsFilters,
  };
}
